import type { Hero, HeroSkill, ActiveSkillEffect, ActiveBuff } from "@/types/hero.types"
import type { Enemy, StatusEffect } from "@/types/enemy.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { Position, EntanglingRootsEffect, PendingAoeWave, AoeVisualEffect } from "@/types/game.types"
import { generateId } from "@/utils/id-generator"
import { distanceSquared } from "@/utils/collision"
import { TILE_SIZE } from "@/data/game-settings"
import { gameNow } from "@/utils/game-clock"
import type { HeroInventory } from "@/types/item.types"
import { getEquipmentCooldownReduction, getEquipmentCritChance } from "./item-system"
import {
  createSummonedWolf,
  createSummonedFox,
  createSummonedBoar,
  createSummonedTower,
  healSummon,
  getSummonsByType,
  getSummonsByOwner,
} from "@/systems/summon-system"

// Get the current level stats for a skill
export const getSkillLevelStats = (skill: HeroSkill) => {
  const levelIndex = Math.max(0, Math.min(skill.currentLevel - 1, skill.levels.length - 1))
  return skill.levels[levelIndex]
}

// Check if a skill can be used
export const canUseSkill = (hero: Hero, skillId: string): { canUse: boolean; reason?: string } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { canUse: false, reason: "Skill not found" }
  if (!skill.isUnlocked) return { canUse: false, reason: "Skill not unlocked" }
  if (skill.currentCooldown > 0) return { canUse: false, reason: "On cooldown" }

  const stats = getSkillLevelStats(skill)
  if (hero.mana < stats.manaCost) return { canUse: false, reason: "Not enough mana" }

  return { canUse: true }
}

// Activate a skillshot skill in the direction the hero is facing
export const activateSkillshot = (
  hero: Hero,
  skillId: string,
): { hero: Hero; effect: ActiveSkillEffect | null } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { hero, effect: null }

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return { hero, effect: null }

  const stats = getSkillLevelStats(skill)

  // Calculate direction based on hero rotation
  const direction = {
    x: Math.cos(hero.rotation),
    y: Math.sin(hero.rotation),
  }

  // Create the skill effect
  const effect: ActiveSkillEffect = {
    id: generateId("skill-effect"),
    skillId: skill.id,
    heroId: hero.id,
    position: { ...hero.position },
    direction,
    damage: stats.damage,
    speed: stats.speed,
    distanceTraveled: 0,
    maxDistance: stats.range * TILE_SIZE, // Convert tiles to pixels
    hitEnemies: [],
    timestamp: gameNow(),
  }

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId
      ? { ...s, currentCooldown: stats.cooldown }
      : s
  )

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
  }

  return { hero: updatedHero, effect }
}

// Activate a dash skill - hero dashes in direction they're facing
export const activateDash = (hero: Hero, skillId: string): Hero | null => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return null

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return null

  // Can't dash while already dashing
  if (hero.isDashing) return null

  const stats = getSkillLevelStats(skill)

  // Calculate direction based on hero rotation
  const direction = {
    x: Math.cos(hero.rotation),
    y: Math.sin(hero.rotation),
  }

  // Update hero: consume mana, set cooldown, start dash
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId
      ? { ...s, currentCooldown: stats.cooldown }
      : s
  )

  return {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    isDashing: true,
    dashDirection: direction,
    dashProgress: 0,
    dashMaxDistance: stats.range * TILE_SIZE, // Convert tiles to pixels
    dashDamage: stats.damage,
    dashSpeed: stats.speed * 10, // Scale speed for dash (pixels per frame)
    dashSlowValue: stats.slowValue ?? 0,
    dashSlowDuration: stats.slowDuration ?? 0,
    dashHitEnemies: [],
    // Clear any movement target during dash
    targetPosition: null,
  }
}

// Activate a targeted dash skill - hero dashes toward a specific position
export const activateTargetedDash = (hero: Hero, skillId: string, targetPosition: Position): Hero | null => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return null

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return null

  // Can't dash while already dashing
  if (hero.isDashing) return null

  const stats = getSkillLevelStats(skill)

  // Calculate direction toward target position
  const dx = targetPosition.x - hero.position.x
  const dy = targetPosition.y - hero.position.y
  const distance = Math.sqrt(dx * dx + dy * dy)

  // Normalize direction
  const direction = distance > 0
    ? { x: dx / distance, y: dy / distance }
    : { x: 1, y: 0 } // Default direction if target is at hero position

  // Update hero: consume mana, set cooldown, start dash
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId
      ? { ...s, currentCooldown: stats.cooldown }
      : s
  )

  // Update hero rotation to face the dash direction
  const newRotation = Math.atan2(direction.y, direction.x)

  // Use the actual distance to target if it's shorter than max range
  const maxRange = stats.range * TILE_SIZE
  const actualDashDistance = Math.min(distance, maxRange)

  return {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    rotation: newRotation,
    isDashing: true,
    dashDirection: direction,
    dashProgress: 0,
    dashMaxDistance: actualDashDistance, // Use shorter of actual distance or max range
    dashDamage: stats.damage,
    dashSpeed: stats.speed * 10, // Scale speed for dash (pixels per frame)
    dashSlowValue: stats.slowValue ?? 0,
    dashSlowDuration: stats.slowDuration ?? 0,
    dashHitEnemies: [],
    // Clear any movement target during dash
    targetPosition: null,
    // Trigger skill animation
    animationState: "skill",
    activeSkillAnimation: skillId,
    animationStartTime: gameNow(),
    // Interrupt relaxed idle
    isPlayingRelaxedIdle: false,
  }
}

// Activate a targeted projectile skill - shoots a projectile toward a specific position
export const activateTargetedProjectile = (
  hero: Hero,
  skillId: string,
  targetPosition: Position,
): { hero: Hero; effect: ActiveSkillEffect | null } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { hero, effect: null }

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return { hero, effect: null }

  const stats = getSkillLevelStats(skill)

  // Calculate direction toward target position
  const dx = targetPosition.x - hero.position.x
  const dy = targetPosition.y - hero.position.y
  const distance = Math.sqrt(dx * dx + dy * dy)

  // Normalize direction
  const direction = distance > 0
    ? { x: dx / distance, y: dy / distance }
    : { x: Math.cos(hero.rotation), y: Math.sin(hero.rotation) }

  // Create the skill effect (projectile)
  const effect: ActiveSkillEffect = {
    id: generateId("skill-effect"),
    skillId: skill.id,
    heroId: hero.id,
    position: { ...hero.position },
    direction,
    damage: stats.damage,
    speed: stats.speed,
    distanceTraveled: 0,
    maxDistance: stats.range * TILE_SIZE, // Convert tiles to pixels
    hitEnemies: [],
    timestamp: gameNow(),
  }

  // Update hero: consume mana, set cooldown, face direction
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId
      ? { ...s, currentCooldown: stats.cooldown }
      : s
  )

  const newRotation = Math.atan2(direction.y, direction.x)

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    rotation: newRotation,
    // Trigger skill animation
    animationState: "skill",
    activeSkillAnimation: skillId,
    animationStartTime: gameNow(),
    // Interrupt relaxed idle
    isPlayingRelaxedIdle: false,
  }

  return { hero: updatedHero, effect }
}

/**
 * Tick skill cooldowns down (called every frame).
 *
 * Cooldown reduction is applied here, to the tick rate, rather than at the six
 * places that set `currentCooldown` on activation. One integration point instead
 * of six, and swapping gear mid-cooldown then speeds up or slows the remaining
 * wait instead of leaving it locked to the value captured on cast.
 *
 * A 30% reduction should make a cooldown last 70% as long, so the delta is
 * scaled by 1/(1-0.3). The getter caps the reduction below 1, keeping the
 * divisor non-zero.
 */
export const updateSkillCooldowns = (hero: Hero, delta: number, inventory?: HeroInventory): Hero => {
  const reduction = inventory ? getEquipmentCooldownReduction(inventory) : 0
  const scaledDelta = delta / (1 - reduction)
  let hasChanges = false
  const updatedSkills = hero.skills.map((skill) => {
    if (skill.currentCooldown > 0) {
      hasChanges = true
      return { ...skill, currentCooldown: Math.max(0, skill.currentCooldown - scaledDelta) }
    }
    return skill
  })

  if (!hasChanges) return hero

  return { ...hero, skills: updatedSkills }
}

// Update a skill effect (move it, check for hits)
export const updateSkillEffect = (
  effect: ActiveSkillEffect,
  enemies: Enemy[],
  delta: number,
): { effect: ActiveSkillEffect; hitEnemyIds: string[] } => {
  // Calculate progress for easing (0 = start, 1 = end)
  const progress = effect.maxDistance > 0 ? effect.distanceTraveled / effect.maxDistance : 0

  // Ease-out: start at 1.5x speed, end at 0.5x speed
  const speedMultiplier = 1.5 - progress

  const moveDistance = effect.speed * delta * speedMultiplier

  // Move the effect
  const newPosition: Position = {
    x: effect.position.x + effect.direction.x * moveDistance,
    y: effect.position.y + effect.direction.y * moveDistance,
  }

  const newDistanceTraveled = effect.distanceTraveled + moveDistance

  // Check for hits against enemies
  const hitEnemyIds: string[] = []
  const hitRadius = 24 // Hit detection radius
  const hitRadiusSq = hitRadius * hitRadius

  for (const enemy of enemies) {
    // Skip already hit enemies
    if (effect.hitEnemies.includes(enemy.id)) continue

    const distSq = distanceSquared(newPosition, enemy.position)
    if (distSq <= hitRadiusSq) {
      hitEnemyIds.push(enemy.id)
    }
  }

  return {
    effect: {
      ...effect,
      position: newPosition,
      distanceTraveled: newDistanceTraveled,
      hitEnemies: [...effect.hitEnemies, ...hitEnemyIds],
    },
    hitEnemyIds,
  }
}

// Check if a skill effect has expired
export const isSkillEffectExpired = (effect: ActiveSkillEffect): boolean => {
  return effect.distanceTraveled >= effect.maxDistance
}

// Check if hero can upgrade a skill (has skill points and skill is upgradable)
// Non-ultimate skills: can be upgraded every time another skill has been upgraded
// Ultimate skills: keep existing restriction (every 6 hero levels)
export const canUpgradeSkill = (hero: Hero, skillId: string): boolean => {
  if (hero.skillPoints <= 0) return false

  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return false
  // Ultimate skills have special unlock requirements handled by canUpgradeUltimate
  if (skill.type === "ultimate" && !skill.isUnlocked) return false
  if (skill.currentLevel >= skill.maxLevel) return false

  // For non-ultimate skills: can only upgrade if another skill was upgraded more recently
  // (or if no skills have been upgraded yet)
  if (skill.type !== "ultimate" && skill.lastUpgradedAtLevel !== undefined) {
    // Find the most recently upgraded skill (highest lastUpgradedAtLevel)
    let mostRecentUpgradeLevel = 0
    let mostRecentlyUpgradedSkillId: string | null = null

    for (const s of hero.skills) {
      if (s.lastUpgradedAtLevel !== undefined && s.lastUpgradedAtLevel > mostRecentUpgradeLevel) {
        mostRecentUpgradeLevel = s.lastUpgradedAtLevel
        mostRecentlyUpgradedSkillId = s.id
      }
    }

    // If this skill was the most recently upgraded, it can't be upgraded again
    // until another skill is upgraded
    if (mostRecentlyUpgradedSkillId === skillId) {
      return false
    }
  }

  return true
}

// Upgrade a skill to the next level (costs 1 skill point)
export const upgradeSkill = (hero: Hero, skillId: string): Hero | null => {
  if (!canUpgradeSkill(hero, skillId)) return null

  const skillIndex = hero.skills.findIndex((s) => s.id === skillId)
  if (skillIndex === -1) return null

  const skill = hero.skills[skillIndex]

  const updatedSkills = [...hero.skills]
  updatedSkills[skillIndex] = {
    ...skill,
    currentLevel: skill.currentLevel + 1,
    // Unlock skill when upgrading from level 0 to level 1
    isUnlocked: true,
    // Track when skill was upgraded for 2-level cooldown
    lastUpgradedAtLevel: hero.level,
  }

  return {
    ...hero,
    skills: updatedSkills,
    skillPoints: hero.skillPoints - 1, // Consume 1 skill point
  }
}

/**
 * Upgrade a skill paid for with gold (the shrine) instead of a skill point.
 * Skips the skill-point economy and the "upgrade something else first" rotation
 * rule, both of which only apply to level-up points.
 */
export const upgradeSkillWithGold = (hero: Hero, skillId: string): Hero | null => {
  const skillIndex = hero.skills.findIndex((s) => s.id === skillId)
  if (skillIndex === -1) return null

  const skill = hero.skills[skillIndex]
  if (skill.currentLevel >= skill.maxLevel) return null

  const updatedSkills = [...hero.skills]
  updatedSkills[skillIndex] = {
    ...skill,
    currentLevel: skill.currentLevel + 1,
    isUnlocked: true,
  }

  return { ...hero, skills: updatedSkills }
}

// Get skill by hotkey
export const getSkillByHotkey = (hero: Hero, hotkey: string): HeroSkill | null => {
  return hero.skills.find((s) => s.hotkey === hotkey && s.isUnlocked) || null
}

// Regenerate mana over time (called every frame)
export const regenerateMana = (hero: Hero, delta: number, regenRate: number = 0.5): Hero => {
  if (hero.mana >= hero.maxMana) return hero

  const newMana = Math.min(hero.maxMana, hero.mana + regenRate * delta)
  return { ...hero, mana: newMana }
}

// Get cooldown progress (0 to 1, where 1 is ready)
export const getSkillCooldownProgress = (skill: HeroSkill): number => {
  if (skill.currentCooldown <= 0) return 1
  const stats = getSkillLevelStats(skill)
  return 1 - skill.currentCooldown / stats.cooldown
}

// Get formatted cooldown text
export const getSkillCooldownText = (skill: HeroSkill): string => {
  if (skill.currentCooldown <= 0) return ""
  const seconds = Math.ceil(skill.currentCooldown / 60)
  return `${seconds}s`
}

// Activate a summon skill - spawns units or heals/respawns existing ones
// targetPosition is optional - if provided, summon spawns there (for targetable summons like forge tower)
export const activateSummon = (
  hero: Hero,
  skillId: string,
  existingSummons: SummonedUnit[],
  targetPosition?: Position,
): { hero: Hero; newSummons: SummonedUnit[]; removedSummonIds: string[] } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { hero, newSummons: [], removedSummonIds: [] }

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return { hero, newSummons: [], removedSummonIds: [] }

  const stats = getSkillLevelStats(skill)

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  // Determine if this is a skill with a dedicated animation (forge-turret for Siegesmith)
  const hasSkillAnimation = skillId === "forge-turret"

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    // Trigger skill animation for specific summon skills
    ...(hasSkillAnimation && {
      animationState: "skill" as const,
      activeSkillAnimation: skillId,
      animationStartTime: gameNow(),
      isPlayingRelaxedIdle: false,
    }),
  }

  // Determine spawn position - use target position if provided, otherwise hero position
  const spawnPosition = targetPosition || hero.position

  // Handle multi-type summons (Wildkin Call - fox + boar)
  // Always removes existing summons and spawns new ones at tiles left/right of hero
  if (stats.summonTypes && stats.summonTypes.length > 0) {
    // Get all owned summons (any type) to remove them
    const ownedSummons = getSummonsByOwner(existingSummons, hero.id)
    const removedSummonIds = ownedSummons.map((s) => s.id)

    // Calculate spawn positions: one tile to the left, one tile to the right of hero
    const leftPosition: Position = {
      x: hero.position.x - TILE_SIZE,
      y: hero.position.y,
    }
    const rightPosition: Position = {
      x: hero.position.x + TILE_SIZE,
      y: hero.position.y,
    }

    // Spawn all summon types - alternate between left and right positions
    const newSummons: SummonedUnit[] = []
    for (let i = 0; i < stats.summonTypes.length; i++) {
      const summonSpec = stats.summonTypes[i]
      // Alternate: first summon goes left, second goes right
      const spawnPos = i % 2 === 0 ? leftPosition : rightPosition

      if (summonSpec.type === "fox") {
        newSummons.push(createSummonedFox(spawnPos, hero.id, {
          damage: stats.damage,
          health: summonSpec.health,
          speed: 0,
          range: stats.range,
          level: skill.currentLevel,
        }, i))
      } else if (summonSpec.type === "boar") {
        newSummons.push(createSummonedBoar(spawnPos, hero.id, {
          damage: stats.damage,
          health: summonSpec.health,
          speed: 0,
          range: stats.range,
          level: skill.currentLevel,
        }, i))
      } else if (summonSpec.type === "wolf") {
        newSummons.push(createSummonedWolf(spawnPos, hero.id, {
          damage: stats.damage,
          health: summonSpec.health,
          speed: 0,
          range: stats.range,
          level: skill.currentLevel,
        }, i))
      }
    }
    return { hero: updatedHero, newSummons, removedSummonIds }
  }

  // Handle single-type summons (legacy wolf/siege-tower)
  const summonType = stats.summonType || "wolf"
  const summonCount = stats.summonCount || 1
  const summonHealth = stats.summonHealth || 100

  // Get existing summons of this type owned by the hero
  const ownedSummons = getSummonsByType(existingSummons, hero.id, summonType)

  // Handle siege tower: allow up to maxSummons active, create one at a time
  if (summonType === "siege-tower") {
    const maxSummons = stats.maxSummons || 1

    // If at max capacity, destroy oldest one to make room
    let removedSummonIds: string[] = []
    if (ownedSummons.length >= maxSummons) {
      // Remove the oldest turret (first in array)
      removedSummonIds = [ownedSummons[0].id]
    }

    const newTower = createSummonedTower(spawnPosition, hero.id, {
      damage: stats.damage,
      health: summonHealth,
      speed: 0,
      range: stats.range,
      level: skill.currentLevel,
    })
    return { hero: updatedHero, newSummons: [newTower], removedSummonIds }
  }

  // Handle wolves: heal existing and respawn dead ones
  if (ownedSummons.length > 0) {
    // Heal all existing wolves
    const healedSummons = ownedSummons.map((s) => healSummon(s))

    // Count how many wolves we need to spawn
    const aliveCount = ownedSummons.length
    const toSpawn = summonCount - aliveCount

    const newWolves: SummonedUnit[] = []
    for (let i = 0; i < toSpawn; i++) {
      newWolves.push(
        createSummonedWolf(hero.position, hero.id, {
          damage: stats.damage,
          health: summonHealth,
          speed: stats.speed,
          range: stats.range,
          level: skill.currentLevel,
        }, aliveCount + i),
      )
    }

    // Return healed wolves as "new" so they get updated in state
    return {
      hero: updatedHero,
      newSummons: [...healedSummons, ...newWolves],
      removedSummonIds: ownedSummons.map((s) => s.id), // Remove old wolves, replaced with healed
    }
  }

  // No existing summons - spawn new ones
  const newSummons: SummonedUnit[] = []
  for (let i = 0; i < summonCount; i++) {
    if (summonType === "wolf") {
      newSummons.push(
        createSummonedWolf(hero.position, hero.id, {
          damage: stats.damage,
          health: summonHealth,
          speed: stats.speed,
          range: stats.range,
          level: skill.currentLevel,
        }, i),
      )
    }
  }

  return { hero: updatedHero, newSummons, removedSummonIds: [] }
}

// Activate an AoE skill - creates three waves that radiate outward
// Wave 1 (immediate): 50% damage, full range
// Wave 2 (delayed): 25% damage, full range
// Wave 3 (more delayed): 25% damage, full range
export const activateAOE = (
  hero: Hero,
  skillId: string,
  enemies: Enemy[],
): { hero: Hero; affectedEnemies: Enemy[]; pendingWaves: PendingAoeWave[]; visualEffects: AoeVisualEffect[] } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { hero, affectedEnemies: [], pendingWaves: [], visualEffects: [] }

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return { hero, affectedEnemies: [], pendingWaves: [], visualEffects: [] }

  const stats = getSkillLevelStats(skill)
  const rangePixels = stats.range * TILE_SIZE
  const rangeSquared = rangePixels * rangePixels

  // Wave timing (in frames, 60fps)
  // wave 1 lands immediately, so it needs no delay constant
  const WAVE_2_DELAY = 30 // 0.5 seconds
  const WAVE_3_DELAY = 60 // 1.0 seconds

  // Calculate damage for each wave
  const wave1Damage = Math.floor(stats.damage * 0.5) // 50%
  const wave2Damage = Math.floor(stats.damage * 0.25) // 25%
  const wave3Damage = stats.damage - wave1Damage - wave2Damage // remaining (25% or so)

  // Create pending waves for delayed damage
  const pendingWaves: PendingAoeWave[] = [
    {
      id: generateId("aoe-wave"),
      position: { ...hero.position },
      radius: rangePixels,
      damage: wave2Damage,
      delayFrames: WAVE_2_DELAY,
      skillId,
      slowValue: stats.slowValue,
      slowDuration: stats.slowDuration,
      waveIndex: 1,
    },
    {
      id: generateId("aoe-wave"),
      position: { ...hero.position },
      radius: rangePixels,
      damage: wave3Damage,
      delayFrames: WAVE_3_DELAY,
      skillId,
      slowValue: stats.slowValue,
      slowDuration: stats.slowDuration,
      waveIndex: 2,
    },
  ]

  // Create visual effects for all three waves
  const visualEffects: AoeVisualEffect[] = [
    {
      id: generateId("aoe-visual"),
      position: { ...hero.position },
      radius: rangePixels,
      color: "rgba(255, 255, 255, 0.5)",
      duration: 20,
      remainingFrames: 20,
      waveIndex: 0,
      intensity: 1.0, // First wave is most intense
    },
    {
      id: generateId("aoe-visual"),
      position: { ...hero.position },
      radius: rangePixels,
      color: "rgba(255, 255, 255, 0.3)",
      duration: 20,
      remainingFrames: 20 + WAVE_2_DELAY, // Start later
      waveIndex: 1,
      intensity: 0.6,
    },
    {
      id: generateId("aoe-visual"),
      position: { ...hero.position },
      radius: rangePixels,
      color: "rgba(255, 255, 255, 0.2)",
      duration: 20,
      remainingFrames: 20 + WAVE_3_DELAY, // Start even later
      waveIndex: 2,
      intensity: 0.4,
    },
  ]

  // Apply first wave damage immediately
  const affectedEnemies: Enemy[] = []
  for (const enemy of enemies) {
    if (enemy.health <= 0 || enemy.reachedEnd) continue

    const distSq = distanceSquared(hero.position, enemy.position)
    if (distSq <= rangeSquared) {
      // Apply first wave damage (50%)
      let updatedEnemy = {
        ...enemy,
        health: Math.max(0, enemy.health - wave1Damage),
      }

      // Apply slow effect with first wave
      if (stats.slowValue !== undefined && stats.slowDuration !== undefined) {
        const slowEffect: StatusEffect = {
          id: generateId("slow"),
          type: "slow",
          value: stats.slowValue,
          duration: stats.slowDuration,
          maxDuration: stats.slowDuration,
        }

        updatedEnemy = {
          ...updatedEnemy,
          statusEffects: [...(updatedEnemy.statusEffects || []), slowEffect],
        }
      }

      affectedEnemies.push(updatedEnemy)
    }
  }

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    // Trigger skill animation
    animationState: "skill",
    activeSkillAnimation: skillId,
    animationStartTime: gameNow(),
    // For tremor-slam, start with "pre" phase (jump animation)
    skillAnimationPhase: skillId === "tremor-slam" ? "pre" : undefined,
    // Interrupt relaxed idle
    isPlayingRelaxedIdle: false,
  }

  return { hero: updatedHero, affectedEnemies, pendingWaves, visualEffects }
}

// Apply damage from a pending AOE wave
export const applyAoeWaveDamage = (
  wave: PendingAoeWave,
  enemies: Enemy[],
): Enemy[] => {
  const rangeSquared = wave.radius * wave.radius
  const affectedEnemies: Enemy[] = []

  for (const enemy of enemies) {
    if (enemy.health <= 0 || enemy.reachedEnd) continue

    const distSq = distanceSquared(wave.position, enemy.position)
    if (distSq <= rangeSquared) {
      const updatedEnemy = {
        ...enemy,
        health: Math.max(0, enemy.health - wave.damage),
      }

      // Only apply slow on first wave (already applied above)
      // Subsequent waves just deal damage
      affectedEnemies.push(updatedEnemy)
    }
  }

  return affectedEnemies
}

// Activate Entangling Roots - targets a position, roots nearby enemies, and creates a persistent DoT effect
export const activateEntanglingRoots = (
  hero: Hero,
  skillId: string,
  targetPosition: Position,
  enemies: Enemy[],
): { hero: Hero; rootsEffect: EntanglingRootsEffect | null; updatedEnemy: Enemy | null } => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return { hero, rootsEffect: null, updatedEnemy: null }

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return { hero, rootsEffect: null, updatedEnemy: null }

  const stats = getSkillLevelStats(skill)

  // Check if target position is in range
  const rangePixels = stats.range * TILE_SIZE
  const distSq = distanceSquared(hero.position, targetPosition)
  if (distSq > rangePixels * rangePixels) {
    return { hero, rootsEffect: null, updatedEnemy: null }
  }

  // Find enemy at target position (optional - ground targeting allows no enemy)
  const targetEnemy = enemies.find((e) => {
    if (e.health <= 0 || e.isDying) return false
    const enemyDistSq = distanceSquared(e.position, targetPosition)
    return enemyDistSq < 900 // 30px radius to find nearby enemy
  })

  const rootDuration = stats.rootDuration || 300 // 5 seconds at 60fps
  const dotTicks = stats.dotTicks || 5
  const tickInterval = Math.floor(rootDuration / dotTicks)

  let updatedEnemy: Enemy | null = null

  // If enemy found at position, apply root and initial damage
  if (targetEnemy) {
    const rootEffect: StatusEffect = {
      id: generateId("root"),
      type: "root",
      value: 0, // Root is binary - enemy can't move
      duration: rootDuration,
      maxDuration: rootDuration,
    }

    updatedEnemy = {
      ...targetEnemy,
      health: Math.max(0, targetEnemy.health - stats.damage),
      statusEffects: [...(targetEnemy.statusEffects || []), rootEffect],
      lastDamageTime: gameNow(),
    }
  }

  // Create the persistent roots effect for DoT and AoE at target position
  const rootsEffect: EntanglingRootsEffect = {
    id: generateId("roots"),
    position: { ...targetPosition },
    targetEnemyId: targetEnemy?.id || null,
    heroId: hero.id,
    dotDamage: stats.dotDamage || 0,
    dotTicksRemaining: dotTicks,
    tickInterval,
    tickCooldown: tickInterval, // First tick happens after interval
    aoeRadius: (stats.aoeRadius || 1.5) * TILE_SIZE,
    aoeDamage: stats.aoeDamage || 0,
    rootDuration,
    rootRemaining: rootDuration,
    timestamp: gameNow(),
    rootedEnemyIds: targetEnemy ? [targetEnemy.id] : [], // Track which enemies are already rooted
  }

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
  }

  return { hero: updatedHero, rootsEffect, updatedEnemy }
}

// Update an EntanglingRootsEffect - process DoT ticks, AoE damage, and root new enemies
export const updateEntanglingRootsEffect = (
  effect: EntanglingRootsEffect,
  enemies: Enemy[],
  delta: number,
): {
  effect: EntanglingRootsEffect
  damagedEnemies: Array<{ id: string; damage: number }>
  newlyRootedEnemyIds: string[] // IDs of enemies that just entered the roots area
} => {
  const damagedEnemies: Array<{ id: string; damage: number }> = []
  const newlyRootedEnemyIds: string[] = []
  const updatedRootedEnemyIds = [...effect.rootedEnemyIds]

  let newTickCooldown = effect.tickCooldown - delta
  let newTicksRemaining = effect.dotTicksRemaining
  const newRootRemaining = effect.rootRemaining - delta

  const aoeRadiusSq = effect.aoeRadius * effect.aoeRadius

  // Check for new enemies entering the roots area (every frame, not just on ticks)
  for (const enemy of enemies) {
    if (enemy.health <= 0 || enemy.reachedEnd) continue
    if (updatedRootedEnemyIds.includes(enemy.id)) continue // Already rooted by this effect

    const distSq = distanceSquared(effect.position, enemy.position)
    if (distSq <= aoeRadiusSq) {
      // New enemy entered the roots - add to rooted list
      updatedRootedEnemyIds.push(enemy.id)
      newlyRootedEnemyIds.push(enemy.id)
    }
  }

  // Check if it's time for a damage tick
  if (newTickCooldown <= 0 && newTicksRemaining > 0) {
    // Apply DoT damage to primary target
    const targetEnemy = enemies.find((e) => e.id === effect.targetEnemyId)
    if (targetEnemy && targetEnemy.health > 0) {
      damagedEnemies.push({ id: targetEnemy.id, damage: effect.dotDamage })
    }

    // Apply AoE damage to nearby enemies (not the primary target)
    for (const enemy of enemies) {
      if (enemy.id === effect.targetEnemyId) continue // Skip primary target
      if (enemy.health <= 0 || enemy.reachedEnd) continue

      const distSq = distanceSquared(effect.position, enemy.position)
      if (distSq <= aoeRadiusSq) {
        damagedEnemies.push({ id: enemy.id, damage: effect.aoeDamage })
      }
    }

    newTicksRemaining -= 1
    newTickCooldown = effect.tickInterval
  }

  return {
    effect: {
      ...effect,
      tickCooldown: newTickCooldown,
      dotTicksRemaining: newTicksRemaining,
      rootRemaining: newRootRemaining,
      rootedEnemyIds: updatedRootedEnemyIds,
    },
    damagedEnemies,
    newlyRootedEnemyIds,
  }
}

// Check if an EntanglingRootsEffect has expired
export const isEntanglingRootsExpired = (effect: EntanglingRootsEffect): boolean => {
  return effect.rootRemaining <= 0 && effect.dotTicksRemaining <= 0
}

// Get passive skill stats from a hero (returns critChance and critMultiplier if available)
export const getPassiveSkillStats = (hero: Hero): { critChance: number; critMultiplier: number } => {
  let critChance = 0
  let critMultiplier = 1

  for (const skill of hero.skills) {
    // Only process unlocked passive skills with at least level 1
    if (skill.type !== "passive" || !skill.isUnlocked || skill.currentLevel < 1) continue

    const stats = getSkillLevelStats(skill)
    if (stats.critChance !== undefined) {
      critChance = Math.max(critChance, stats.critChance)
    }
    if (stats.critMultiplier !== undefined) {
      critMultiplier = Math.max(critMultiplier, stats.critMultiplier)
    }
  }

  return { critChance, critMultiplier }
}

// Calculate damage with critical hit chance (returns { damage, isCritical })
export const calculateDamageWithCrit = (
  baseDamage: number,
  critChance: number,
  critMultiplier: number,
): { damage: number; isCritical: boolean } => {
  const isCritical = Math.random() < critChance
  const damage = isCritical ? Math.floor(baseDamage * critMultiplier) : baseDamage
  return { damage, isCritical }
}

/**
 * Multiplier applied when a crit comes from gear rather than a passive skill.
 *
 * Passive crit skills carry their own multiplier, which scales all the way to
 * 20x at max rank. getPassiveSkillStats reports 1 when the hero has no such
 * passive, so equipment crit chance would otherwise roll a "critical" that deals
 * exactly normal damage. A hero with a crit passive keeps that passive's
 * multiplier; gear only ever contributes chance.
 */
export const BASE_CRIT_MULTIPLIER = 2

/**
 * Hero attack damage, folding in passive-skill crit and equipment crit chance.
 *
 * Chance from both sources adds together; getEquipmentCritChance clamps its own
 * contribution, and calculateDamageWithCrit compares against a single roll, so a
 * fully stacked build tops out at always-crit rather than overflowing.
 */
export const applyHeroDamageWithPassives = (
  hero: Hero,
  baseDamage: number,
  inventory?: HeroInventory,
): { damage: number; isCritical: boolean } => {
  const { critChance: passiveChance, critMultiplier: passiveMultiplier } = getPassiveSkillStats(hero)
  const equipmentChance = inventory ? getEquipmentCritChance(inventory) : 0
  const critChance = Math.min(1, passiveChance + equipmentChance)
  const critMultiplier = passiveMultiplier > 1 ? passiveMultiplier : BASE_CRIT_MULTIPLIER
  return calculateDamageWithCrit(baseDamage, critChance, critMultiplier)
}

// Activate a buff skill (like Empower Armor)
export const activateBuff = (hero: Hero, skillId: string): Hero | null => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return null

  const { canUse } = canUseSkill(hero, skillId)
  if (!canUse) return null

  const stats = getSkillLevelStats(skill)

  // Create the active buff
  const newBuff: ActiveBuff = {
    id: generateId("buff"),
    skillId: skill.id,
    remainingDuration: stats.buffDuration || 900, // default 15 seconds
    healOnHitChance: stats.healOnHitChance || 0,
    healAmount: stats.healAmount || 0,
    damageReduction: stats.damageReduction || 0,
  }

  // Update hero: consume mana, set cooldown, add buff
  const updatedSkills = hero.skills.map((s) =>
    s.id === skillId ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  return {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
    activeBuffs: [...hero.activeBuffs, newBuff],
  }
}

// Update active buffs (tick down durations, remove expired)
export const updateActiveBuffs = (hero: Hero, delta: number): Hero => {
  if (hero.activeBuffs.length === 0) return hero

  const updatedBuffs = hero.activeBuffs
    .map((buff) => ({ ...buff, remainingDuration: buff.remainingDuration - delta }))
    .filter((buff) => buff.remainingDuration > 0)

  if (updatedBuffs.length === hero.activeBuffs.length) {
    // Check if any actually changed
    let changed = false
    for (let i = 0; i < updatedBuffs.length; i++) {
      if (updatedBuffs[i].remainingDuration !== hero.activeBuffs[i].remainingDuration) {
        changed = true
        break
      }
    }
    if (!changed) return hero
  }

  return { ...hero, activeBuffs: updatedBuffs }
}

// Check if hero should heal from being hit (Empower Armor effect)
export const checkHealOnHit = (hero: Hero): { shouldHeal: boolean; healAmount: number } => {
  // Check all active buffs for heal-on-hit chance
  for (const buff of hero.activeBuffs) {
    if (buff.healOnHitChance > 0 && Math.random() < buff.healOnHitChance) {
      return { shouldHeal: true, healAmount: buff.healAmount }
    }
  }
  return { shouldHeal: false, healAmount: 0 }
}

// Check if hero has an active buff of a specific skill
export const hasActiveBuff = (hero: Hero, skillId: string): boolean => {
  return hero.activeBuffs.some((buff) => buff.skillId === skillId)
}

// Get interest bonus from passive skills (Dwarven Treasury)
export const getInterestBonus = (hero: Hero): number => {
  let bonus = 0

  for (const skill of hero.skills) {
    // Only process unlocked passive skills with at least level 1
    if (skill.type !== "passive" || !skill.isUnlocked || skill.currentLevel < 1) continue

    const stats = getSkillLevelStats(skill)
    if (stats.interestBonus !== undefined) {
      bonus += stats.interestBonus
    }
  }

  return bonus
}

// Get the gold cost for upgrading a skill to the next level (for skills like Dwarven Treasury)
export const getSkillGoldCost = (hero: Hero, skillId: string): number => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return 0

  // Get the stats for the next level
  const nextLevel = skill.currentLevel + 1
  if (nextLevel > skill.maxLevel || nextLevel > skill.levels.length) return 0

  const nextLevelStats = skill.levels[nextLevel - 1]
  return nextLevelStats.goldCost || 0
}

// Get the mana cost for upgrading a passive skill (consumed when upgrading)
export const getSkillUpgradeManaCost = (hero: Hero, skillId: string): number => {
  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return 0

  // Get the stats for the next level
  const nextLevel = skill.currentLevel + 1
  if (nextLevel > skill.maxLevel || nextLevel > skill.levels.length) return 0

  const nextLevelStats = skill.levels[nextLevel - 1]
  return nextLevelStats.manaCost || 0
}

// === ULTIMATE SKILL FUNCTIONS ===

// Check if an ultimate skill can be upgraded (every 6 hero levels)
export const canUpgradeUltimate = (hero: Hero, skillId: string): boolean => {
  if (hero.skillPoints <= 0) return false

  const skill = hero.skills.find((s) => s.id === skillId)
  if (!skill) return false
  if (skill.type !== "ultimate") return false

  // Calculate max ultimate level based on hero level
  // Level 6 = ult level 1, level 12 = ult level 2, etc.
  const maxUltimateLevel = Math.floor(hero.level / 6)
  if (maxUltimateLevel <= 0) return false

  // Can upgrade if current level is less than max allowed
  return skill.currentLevel < maxUltimateLevel
}

// Get the max upgrade level for an ultimate based on hero level
export const getMaxUltimateLevelForHero = (heroLevel: number): number => {
  return Math.floor(heroLevel / 6)
}

// Get damage reflection stats from Backfire Aura ultimate
export const getDamageReflection = (hero: Hero): { reflection: number; radius: number } => {
  const skill = hero.skills.find((s) => s.id === "backfire-aura")
  if (!skill || !skill.isUnlocked || skill.currentLevel < 1) {
    return { reflection: 0, radius: 0 }
  }

  const stats = getSkillLevelStats(skill)
  return {
    reflection: stats.damageReflection || 0,
    radius: stats.aoeRadius || 0,
  }
}

// Check if a position is within the ranger's backfire aura
export const isWithinBackfireAura = (
  hero: Hero,
  unitPosition: Position,
): boolean => {
  const { radius } = getDamageReflection(hero)
  if (radius <= 0) return false

  const dx = unitPosition.x - hero.position.x
  const dy = unitPosition.y - hero.position.y
  const distSq = dx * dx + dy * dy

  return distSq <= radius * radius
}

// Get Trader's Gambit skill level (for shop unlock checks)
export const getTradersGambitLevel = (hero: Hero): number => {
  const skill = hero.skills.find((s) => s.id === "traders-gambit")
  if (!skill || !skill.isUnlocked) return 0
  return skill.currentLevel
}

// Get Embolden skill stats (for tower buff)
export const getEmboldenStats = (
  hero: Hero,
): { canUse: boolean; attackSpeedMultiplier: number; duration: number; radius: number; manaCost: number } => {
  const skill = hero.skills.find((s) => s.id === "embolden")
  if (!skill || !skill.isUnlocked || skill.currentLevel < 1) {
    return { canUse: false, attackSpeedMultiplier: 1, duration: 0, radius: 0, manaCost: 0 }
  }

  const { canUse } = canUseSkill(hero, "embolden")
  const stats = getSkillLevelStats(skill)

  return {
    canUse,
    attackSpeedMultiplier: stats.towerFireRateMultiplier || 0.5,
    duration: stats.buffDuration || 300,
    radius: stats.aoeRadius || 192,
    manaCost: stats.manaCost,
  }
}

// Activate Embolden ultimate (Paladin) - affects towers in radius
export const activateEmbolden = (
  hero: Hero,
): { hero: Hero; activated: boolean; duration: number; radius: number; attackSpeedMultiplier: number } => {
  const skill = hero.skills.find((s) => s.id === "embolden")
  if (!skill) return { hero, activated: false, duration: 0, radius: 0, attackSpeedMultiplier: 1 }

  const { canUse } = canUseSkill(hero, "embolden")
  if (!canUse) return { hero, activated: false, duration: 0, radius: 0, attackSpeedMultiplier: 1 }

  const stats = getSkillLevelStats(skill)

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === "embolden" ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
  }

  return {
    hero: updatedHero,
    activated: true,
    duration: stats.buffDuration || 300,
    radius: stats.aoeRadius || 192,
    attackSpeedMultiplier: stats.towerFireRateMultiplier || 0.5,
  }
}

// Activate Trader's Gambit ultimate (Siegesmith) - get spawn item level
export const activateTradersGambit = (
  hero: Hero,
): { hero: Hero; activated: boolean; spawnItemLevel: number } => {
  const skill = hero.skills.find((s) => s.id === "traders-gambit")
  if (!skill) return { hero, activated: false, spawnItemLevel: 0 }

  const { canUse } = canUseSkill(hero, "traders-gambit")
  if (!canUse) return { hero, activated: false, spawnItemLevel: 0 }

  const stats = getSkillLevelStats(skill)

  // Update hero: consume mana, set cooldown
  const updatedSkills = hero.skills.map((s) =>
    s.id === "traders-gambit" ? { ...s, currentCooldown: stats.cooldown } : s,
  )

  const updatedHero: Hero = {
    ...hero,
    mana: hero.mana - stats.manaCost,
    skills: updatedSkills,
  }

  return {
    hero: updatedHero,
    activated: true,
    spawnItemLevel: stats.spawnItemLevel || skill.currentLevel,
  }
}
